import { React, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Stock } from "../components/Stock";
import MarketData from "../components/MarketData/MarketData";
import { config } from "../Constants";
import Seo from "../components/Seo";
import { formatCurrency, getDisplayPrice } from "../utils/priceUtils";
import { categoryTitleNoun } from '../utils/productCategories';

const URL = config.url;
const SITE_URL = "https://stockmaid.com";
export const SkuPage = () => {
    const [stock, setStock] = useState({});
    // Only a real 404 from the API means the product does not exist. A transport
    // error or 5xx must NOT turn into noindex: Google renders these pages, and a
    // failed fetch inside its renderer used to write noindex over the server's
    // index,follow, which is how every product page got dropped (issue #270).
    const [notFound, setNotFound] = useState(false);
    const { sku } = useParams();
    
    const fetchStocks = useCallback(async () => {
        try {
            const response = await fetch(`${URL}/api/v1/stocks/sku/${sku}`);
            if (!response.ok) {
                setNotFound(response.status === 404);
                setStock({});
                return;
            }
            const apiResponse = await response.json();
            const stockData = apiResponse.data || {};
            setStock(stockData);
            setNotFound(!stockData?.sku);
        } catch (error) {
            console.error("Error fetching stock data:", error);
            setNotFound(false);
        }
    }, [sku]);

    useEffect(() => {
        fetchStocks();
        // Keep "Last checked" honest while the page stays open: Best Buy is polled
        // about every minute, so a minute is the finest refresh that adds information.
        const id = setInterval(fetchStocks, 60_000);
        return () => clearInterval(id);
    }, [fetchStocks]);

    const stockKind = categoryTitleNoun(stock?.productCategory);
    const stockName = stock?.name || stockKind;
    const displayPrice = getDisplayPrice(stock);
    const price = formatCurrency(displayPrice);
    const title = stock?.sku
        ? `${stockName} Stock Tracker | StockMaid`
        : `${stockKind} Stock Tracker | StockMaid`;
    const description = stock?.sku
        ? `Track ${stockName} availability at Best Buy. ${price ? `Current price: ${price}. ` : ""}Get instant restock alerts with StockMaid.`
        : `Track ${stockKind} availability at Best Buy with real-time stock updates and alerts.`;

    const productUrl = `${SITE_URL}/skus/${stock?.sku || sku}`;
    const offerUrl = stock?.url || productUrl;
    const productDescription = stock?.longDescription || description;
    const brandName = stock?.manufacturer || null;
    const jsonLd = stock?.sku ? {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Product",
                name: stockName,
                sku: String(stock.sku),
                image: stock?.image || `${SITE_URL}/logo512.png`,
                description: productDescription,
                ...(brandName ? {
                    brand: {
                        "@type": "Brand",
                        name: brandName
                    }
                } : {}),
                offers: {
                    "@type": "Offer",
                    price: displayPrice,
                    priceCurrency: "USD",
                    availability: stock?.onlineAvailability ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                    url: offerUrl,
                    seller: {
                        "@type": "Organization",
                        name: "Best Buy"
                    }
                }
            },
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: SITE_URL
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "Products",
                        item: `${SITE_URL}/stocks`
                    },
                    {
                        "@type": "ListItem",
                        position: 3,
                        name: stockName,
                        item: productUrl
                    }
                ]
            }
        ]
    } : null;

    return (
        <div>
            <Seo
                title={title}
                description={description}
                canonicalPath={`/skus/${sku}`}
                ogImage={stock?.image || undefined}
                ogType={stock?.sku ? "product" : "website"}
                robots={notFound ? "noindex,nofollow" : "index,follow"}
                jsonLd={jsonLd}
            />
            <Stock stock={stock} key={`stock-${sku}`} />
            {stock?.sku && <MarketData key={`market-data-${stock.sku}`} stock={stock} />}
        </div>
    );
};
